// src/pages/crm/PageCrmPipeline.jsx
import { useState, useEffect, useCallback } from "react";
import * as api from "../../apiService";
import { TRIAL_STATUSES, STATUS_MAP } from "./PageCrmSamples";
import { useToast } from "../../components/Toast";

// ── Stage config ──────────────────────────────────────────────────────────────

const STAGES = [
  { key: "SHORTLISTED",   label: "Shortlisted",   color: "#6366f1", bg: "#eef2ff" },
  { key: "LEAD",          label: "Lead",          color: "#0891b2", bg: "#ecfeff" },
  { key: "TRIAL_DONE",    label: "Trial Done",    color: "#7c3aed", bg: "#f5f3ff" },
  { key: "PROPOSAL_SENT", label: "Proposal Sent", color: "#ca8a04", bg: "#fefce8" },
  { key: "NEGOTIATION",   label: "Negotiation",   color: "#c2410c", bg: "#fff7ed" },
  { key: "WON",           label: "Won",           color: "#15803d", bg: "#f0fdf4" },
  { key: "LOST",          label: "Lost",          color: "#6b7280", bg: "#f9fafb" },
];

const STAGE_KEYS  = STAGES.map(s => s.key);
const STAGE_MAP   = Object.fromEntries(STAGES.map(s => [s.key, s]));
const ACTIVE_KEYS = ["SHORTLISTED", "LEAD", "TRIAL_DONE", "PROPOSAL_SENT", "NEGOTIATION"];

function nextStage(current) {
  const idx = ACTIVE_KEYS.indexOf(current);
  return idx >= 0 && idx < ACTIVE_KEYS.length - 1 ? ACTIVE_KEYS[idx + 1] : null;
}

const COMPANY_BTN = {
  AMI: { label: "AMI", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  AMT: { label: "AMT", color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n == null) return null;
  return "AED " + Number(n).toLocaleString("en-AE", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
}

// ── Searchable account picker ─────────────────────────────────────────────────

function AccountPicker({ accounts, value, onChange, disabled }) {
  const selected = accounts.find(a => String(a.id) === String(value));
  const [query, setQuery] = useState(selected?.name ?? "");
  const [open,  setOpen]  = useState(false);

  const matches = query.trim().length === 0
    ? accounts
    : accounts.filter(a => a.name.toLowerCase().includes(query.toLowerCase()));

  function pick(a) {
    onChange(String(a.id));
    setQuery(a.name);
    setOpen(false);
  }

  function handleInput(e) {
    setQuery(e.target.value);
    onChange("");
    setOpen(true);
  }

  function handleBlur() {
    setTimeout(() => {
      setOpen(false);
      if (!value) setQuery("");
    }, 150);
  }

  const inp = {
    padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6,
    fontSize: 13, width: "100%", boxSizing: "border-box",
    background: disabled ? "#f9fafb" : "#fff",
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        value={query}
        onChange={handleInput}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder="Type to search customers…"
        style={inp}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
          background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          maxHeight: 220, overflowY: "auto", marginTop: 2,
        }}>
          {matches.map(a => (
            <div key={a.id} onMouseDown={() => pick(a)}
              style={{
                padding: "8px 12px", fontSize: 13, cursor: "pointer",
                background: String(a.id) === String(value) ? "#eff6ff" : "transparent",
                color: "#111827",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = String(a.id) === String(value) ? "#eff6ff" : "transparent"}
            >
              <span style={{ fontWeight: 600 }}>{a.name}</span>
              {a.segment && <span style={{ color: "#9ca3af", marginLeft: 8, fontSize: 11 }}>{a.segment}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create / Edit Modal ───────────────────────────────────────────────────────

function OppModal({ opp, accounts, userEmail, canManage, onSave, onClose }) {
  const isEdit = !!opp?.id;
  const [form, setForm] = useState(() => ({
    company:           opp?.company           ?? "",
    customerType:      opp ? (opp.isProspect ? "new" : "existing") : "",
    accountId:         opp?.accountId         ?? "",
    prospectName:      opp?.isProspect ? (opp.accountName ?? "") : "",
    title:             opp?.title             ?? "",
    stage:             opp?.stage             ?? "SHORTLISTED",
    expectedValue:     opp?.expectedValue     != null ? String(opp.expectedValue) : "",
    probability:       opp?.probability       != null ? String(opp.probability)   : "",
    expectedCloseDate: opp?.expectedCloseDate ?? "",
    assignedTo:        opp?.assignedTo        ?? userEmail,
    notes:             opp?.notes             ?? "",
    lostReason:        opp?.lostReason        ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const companyAccounts = form.company
    ? accounts.filter(a => a.companies?.includes(form.company))
    : [];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.company)                                             { setErr("Select a company (AMI or AMT)"); return; }
    if (!form.customerType)                                        { setErr("Select New or Existing customer"); return; }
    if (form.customerType === "existing" && !form.accountId)       { setErr("Select an existing customer"); return; }
    if (form.customerType === "new" && !form.prospectName.trim())  { setErr("Enter the customer name"); return; }
    if (!form.title.trim())                                        { setErr("Title is required"); return; }
    setSaving(true); setErr(null);
    try {
      const body = {
        company:           form.company,
        accountId:         form.customerType === "existing" ? Number(form.accountId) : null,
        prospectName:      form.customerType === "new"      ? form.prospectName.trim() : null,
        title:             form.title.trim(),
        stage:             form.stage,
        expectedValue:     form.expectedValue ? Number(form.expectedValue) : null,
        probability:       form.probability   ? Number(form.probability)   : null,
        expectedCloseDate: form.expectedCloseDate || null,
        assignedTo:        canManage ? (form.assignedTo || userEmail) : userEmail,
        notes:             form.notes || null,
        lostReason:        form.stage === "LOST" ? (form.lostReason || null) : null,
      };
      if (isEdit) await api.updateOpportunity(opp.id, body);
      else        await api.createOpportunity(body);
      onSave();
    } catch (ex) {
      setErr(ex.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const inp = { padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" };
  const sectionLabel = { fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6, color: "#374151" };
  const divider = { borderTop: "1px solid #f3f4f6", paddingTop: 12 };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 24, width: 500, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
          {isEdit ? "Edit Opportunity" : "New Opportunity"}
        </div>
        {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Step 1: Company */}
            <div>
              <label style={sectionLabel}>Company *</label>
              <div style={{ display: "flex", gap: 8 }}>
                {Object.entries(COMPANY_BTN).map(([key, cfg]) => {
                  const active = form.company === key;
                  return (
                    <button key={key} type="button" disabled={isEdit}
                      onClick={() => { set("company", key); set("accountId", ""); }}
                      style={{
                        flex: 1, padding: "10px 0", borderRadius: 8, cursor: isEdit ? "default" : "pointer",
                        fontWeight: 700, fontSize: 14, border: `2px solid ${active ? cfg.color : "#e5e7eb"}`,
                        background: active ? cfg.bg : "#fff", color: active ? cfg.color : "#9ca3af", transition: "all .15s",
                      }}>
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Customer type */}
            {form.company && (
              <div style={divider}>
                <label style={sectionLabel}>Customer *</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[["existing", "Existing Customer"], ["new", "New Customer"]].map(([val, label]) => {
                    const active = form.customerType === val;
                    return (
                      <button key={val} type="button" disabled={isEdit}
                        onClick={() => { set("customerType", val); set("accountId", ""); set("prospectName", ""); }}
                        style={{
                          flex: 1, padding: "8px 0", borderRadius: 7, cursor: isEdit ? "default" : "pointer",
                          fontWeight: 600, fontSize: 13,
                          border: `2px solid ${active ? "#6366f1" : "#e5e7eb"}`,
                          background: active ? "#eef2ff" : "#fff", color: active ? "#6366f1" : "#9ca3af",
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {form.customerType === "existing" && (
                  <AccountPicker accounts={companyAccounts} value={form.accountId}
                    onChange={v => set("accountId", v)} disabled={isEdit} />
                )}
                {form.customerType === "new" && (
                  <input value={form.prospectName} onChange={e => set("prospectName", e.target.value)}
                    style={inp} placeholder="Customer / prospect name" disabled={isEdit} />
                )}
              </div>
            )}

            {/* Step 3: Details */}
            {form.customerType && (
              <div style={divider}>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={sectionLabel}>Title *</label>
                    <input value={form.title} onChange={e => set("title", e.target.value)}
                      style={inp} placeholder="e.g. Heat-seal tape supply 2026" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={sectionLabel}>Stage</label>
                      <select value={form.stage} onChange={e => set("stage", e.target.value)} style={inp}>
                        {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={sectionLabel}>Probability %</label>
                      <input type="number" min={0} max={100} value={form.probability}
                        onChange={e => set("probability", e.target.value)} style={inp} placeholder="0–100" />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={sectionLabel}>Expected Value (AED)</label>
                      <input type="number" min={0} value={form.expectedValue}
                        onChange={e => set("expectedValue", e.target.value)} style={inp} placeholder="0.00" />
                    </div>
                    <div>
                      <label style={sectionLabel}>Expected Close Date</label>
                      <input type="date" value={form.expectedCloseDate}
                        onChange={e => set("expectedCloseDate", e.target.value)} style={inp} />
                    </div>
                  </div>
                  <div>
                    <label style={sectionLabel}>Assigned To</label>
                    <input value={form.assignedTo}
                      onChange={e => canManage && set("assignedTo", e.target.value)}
                      style={{ ...inp, background: canManage ? "#fff" : "#f9fafb", color: canManage ? "#111827" : "#6b7280" }}
                      readOnly={!canManage} placeholder="salesperson email" />
                  </div>
                  <div>
                    <label style={sectionLabel}>Notes</label>
                    <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                      rows={3} style={{ ...inp, resize: "vertical" }} />
                  </div>
                  {form.stage === "LOST" && (
                    <div>
                      <label style={sectionLabel}>Lost Reason</label>
                      <textarea value={form.lostReason} onChange={e => set("lostReason", e.target.value)}
                        rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Why was this lost?" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !form.customerType}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Stage note modal (shown on every stage transition) ────────────────────────

function StageNoteModal({ title, subtitle, noteLabel, noteRequired, confirmLabel, confirmColor, onConfirm, onClose }) {
  const [note,   setNote]   = useState("");
  const [saving, setSaving] = useState(false);
  const canSubmit = !noteRequired || note.trim().length > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    await onConfirm(note.trim() || null);
  }

  const inp = {
    width: "100%", padding: "6px 10px", border: "1px solid #d1d5db",
    borderRadius: 6, fontSize: 13, resize: "vertical", boxSizing: "border-box",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1010,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 24, width: 400, maxWidth: "95vw" }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: subtitle ? 4 : 14 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>{subtitle}</div>
        )}
        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
            {noteLabel ?? "Notes"}{noteRequired && " *"}
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
            placeholder={noteRequired ? "Required" : "Optional — add context for this stage"}
            style={inp} autoFocus />
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn" disabled={saving || !canSubmit}
              style={{
                background: canSubmit ? (confirmColor ?? "#6366f1") : "#d1d5db",
                color: "#fff", border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
              }}>
              {saving ? "Saving…" : (confirmLabel ?? "Confirm")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Details modal ─────────────────────────────────────────────────────────────

const ACTIVE_KEYS_SET = new Set(["SHORTLISTED", "LEAD", "TRIAL_DONE", "PROPOSAL_SENT", "NEGOTIATION"]);

function DetailsModal({ opp, onPass, onFail, onClose }) {
  const history  = Array.isArray(opp.stageHistory) ? opp.stageHistory : [];
  const isTrial  = opp.stage === "TRIAL_DONE";
  const stageCfg = STAGE_MAP[opp.stage] ?? STAGE_MAP["SHORTLISTED"];
  const coyCfg   = COMPANY_BTN[opp.company] ?? {};

  // Sample trials
  const [trials,      setTrials]      = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [sampleForm,  setSampleForm]  = useState({ productName: "", quantity: "", application: "", sentDate: "" });
  const [sampleSaving, setSampleSaving] = useState(false);
  const [sampleErr,   setSampleErr]   = useState(null);

  useEffect(() => {
    api.fetchOpportunityTrials(opp.id)
      .then(setTrials)
      .catch(() => setTrials([]))
      .finally(() => setTrialsLoading(false));
  }, [opp.id]);

  function setSF(k, v) { setSampleForm(f => ({ ...f, [k]: v })); }

  async function saveSample(e) {
    e.preventDefault();
    if (!sampleForm.productName.trim()) { setSampleErr("Product name is required"); return; }
    setSampleSaving(true); setSampleErr(null);
    try {
      await api.createTrial(opp.id, {
        productName:  sampleForm.productName.trim(),
        quantity:     sampleForm.quantity   || null,
        application:  sampleForm.application || null,
        sentDate:     sampleForm.sentDate   || null,
        status:       "SENT",
      });
      setSampleForm({ productName: "", quantity: "", application: "", sentDate: "" });
      setShowForm(false);
      const updated = await api.fetchOpportunityTrials(opp.id);
      setTrials(updated);
    } catch (ex) {
      setSampleErr(ex.message ?? "Save failed");
    } finally {
      setSampleSaving(false);
    }
  }

  async function updateTrialStatus(trial, newStatus) {
    await api.updateTrial(trial.id, {
      productName:   trial.productName,
      quantity:      trial.quantity,
      application:   trial.application,
      sentDate:      trial.sentDate,
      status:        newStatus,
      trialNotes:    trial.trialNotes,
      failureReason: trial.failureReason,
    });
    const updated = await api.fetchOpportunityTrials(opp.id);
    setTrials(updated);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: 24, width: 520,
        maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{opp.title}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#374151", fontWeight: 600 }}>{opp.accountName}</span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
              background: coyCfg.bg, color: coyCfg.color, border: `1px solid ${coyCfg.border}`,
            }}>{opp.company}</span>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
              background: stageCfg.bg, color: stageCfg.color,
            }}>{stageCfg.label}</span>
            {opp.isProspect && (
              <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>Prospect</span>
            )}
          </div>
        </div>

        {/* Summary row */}
        {(opp.expectedValue != null || opp.probability != null || opp.expectedCloseDate) && (
          <div style={{
            display: "flex", gap: 16, background: "#f9fafb", borderRadius: 7,
            padding: "10px 14px", marginBottom: 16, flexWrap: "wrap",
          }}>
            {opp.expectedValue != null && (
              <div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>VALUE</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: stageCfg.color }}>{fmt(opp.expectedValue)}</div>
              </div>
            )}
            {opp.probability != null && (
              <div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>PROBABILITY</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{opp.probability}%</div>
              </div>
            )}
            {opp.expectedCloseDate && (
              <div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>CLOSE DATE</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{fmtDate(opp.expectedCloseDate)}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>ASSIGNED TO</div>
              <div style={{ fontSize: 13, color: "#374151" }}>{opp.assignedTo}</div>
            </div>
          </div>
        )}

        {/* Notes */}
        {opp.notes && (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 12px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>NOTES</div>
            <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>{opp.notes}</div>
          </div>
        )}

        {/* Lost reason */}
        {opp.stage === "LOST" && opp.lostReason && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "10px 12px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#991b1b", marginBottom: 4 }}>LOST REASON</div>
            <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>{opp.lostReason}</div>
          </div>
        )}

        {/* Stage history */}
        <div style={{ fontWeight: 600, fontSize: 12, color: "#374151", marginBottom: 8 }}>Stage History</div>
        {history.length === 0 ? (
          <div style={{ fontSize: 12, color: "#d1d5db", marginBottom: 16, padding: "12px 0" }}>
            No stage transitions recorded yet.
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            {history.map((h, i) => {
              const fromCfg = STAGE_MAP[h.from];
              const toCfg   = STAGE_MAP[h.to];
              return (
                <div key={i} style={{
                  padding: "10px 12px", borderRadius: 6, background: "#f9fafb",
                  marginBottom: 6, borderLeft: `3px solid ${toCfg?.color ?? "#d1d5db"}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: h.note ? 4 : 0 }}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: fromCfg?.color ?? "#374151", fontWeight: 600 }}>
                        {fromCfg?.label ?? h.from}
                      </span>
                      <span style={{ color: "#9ca3af", margin: "0 4px" }}>→</span>
                      <span style={{ color: toCfg?.color ?? "#374151", fontWeight: 600 }}>
                        {toCfg?.label ?? h.to}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{fmtDate(h.at)}</div>
                  </div>
                  {h.note && (
                    <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                      {h.note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Sample Trials */}
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginTop: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>
              Sample Trials {trials.length > 0 && <span style={{ color: "#9ca3af", fontWeight: 400 }}>({trials.length})</span>}
            </div>
            {ACTIVE_KEYS_SET.has(opp.stage) && (
              <button onClick={() => setShowForm(v => !v)}
                style={{ fontSize: 11, color: "#6366f1", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                {showForm ? "Cancel" : "+ Log Sample"}
              </button>
            )}
          </div>

          {/* Inline create form */}
          {showForm && (
            <form onSubmit={saveSample} style={{
              background: "#f9fafb", borderRadius: 7, padding: "12px 14px", marginBottom: 10,
              border: "1px solid #e5e7eb",
            }}>
              {sampleErr && <div style={{ color: "#b91c1c", fontSize: 11, marginBottom: 6 }}>{sampleErr}</div>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>Product *</label>
                  <input value={sampleForm.productName}
                    onChange={e => setSF("productName", e.target.value)}
                    placeholder="e.g. AM-1001 WB adhesive"
                    style={{ padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 12, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>Quantity</label>
                  <input value={sampleForm.quantity}
                    onChange={e => setSF("quantity", e.target.value)}
                    placeholder="e.g. 2 kg"
                    style={{ padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 12, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>Application</label>
                  <input value={sampleForm.application}
                    onChange={e => setSF("application", e.target.value)}
                    placeholder="e.g. Laminating PP to foil"
                    style={{ padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 12, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>Sent Date</label>
                  <input type="date" value={sampleForm.sentDate}
                    onChange={e => setSF("sentDate", e.target.value)}
                    style={{ padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 12, width: "100%", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="submit" className="btn btn-primary" disabled={sampleSaving} style={{ fontSize: 12 }}>
                  {sampleSaving ? "Saving…" : "Log Sample"}
                </button>
              </div>
            </form>
          )}

          {/* Trials list */}
          {trialsLoading && <div style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</div>}
          {!trialsLoading && trials.length === 0 && (
            <div style={{ fontSize: 12, color: "#d1d5db", padding: "8px 0" }}>No samples logged yet.</div>
          )}
          {!trialsLoading && trials.map(t => {
            const sc = STATUS_MAP[t.status] ?? STATUS_MAP["SENT"];
            return (
              <div key={t.id} style={{
                padding: "10px 12px", borderRadius: 6, background: "#f9fafb",
                marginBottom: 6, borderLeft: `3px solid ${sc.color}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#111827" }}>{t.productName}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>
                      {[t.quantity, t.application, t.sentDate ? fmtDate(t.sentDate) : null].filter(Boolean).join(" · ")}
                    </div>
                    {t.trialNotes && (
                      <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic", marginTop: 3 }}>{t.trialNotes}</div>
                    )}
                    {t.failureReason && (
                      <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 3 }}>Reason: {t.failureReason}</div>
                    )}
                  </div>
                  <select value={t.status} onChange={e => updateTrialStatus(t, e.target.value)}
                    style={{
                      padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                      border: `1px solid ${sc.border}`, background: sc.bg, color: sc.color,
                      cursor: "pointer", flexShrink: 0,
                    }}>
                    {TRIAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trial Done — Passed / Failed actions */}
        {isTrial && (
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: "#374151", marginBottom: 10 }}>Trial Outcome</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onPass}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 7,
                  border: "2px solid #bbf7d0", background: "#f0fdf4",
                  color: "#15803d", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>
                Trial Passed → Proposal Sent
              </button>
              <button onClick={onFail}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 7,
                  border: "2px solid #fca5a5", background: "#fef2f2",
                  color: "#b91c1c", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>
                Trial Failed
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Opportunity card ──────────────────────────────────────────────────────────

function OppCard({ opp, onEdit, onAdvance, onWon, onLost, onDetails, onDelete }) {
  const [showActions, setShowActions] = useState(false);
  const stage    = STAGE_MAP[opp.stage] ?? STAGE_MAP["SHORTLISTED"];
  const coyCfg   = COMPANY_BTN[opp.company] ?? {};
  const daysLeft = daysUntil(opp.expectedCloseDate);
  const isActive = ACTIVE_KEYS.includes(opp.stage);
  const isTrial  = opp.stage === "TRIAL_DONE";
  const next     = nextStage(opp.stage);

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
      padding: 12, marginBottom: 8, position: "relative",
      borderLeft: `3px solid ${stage.color}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}>
      {/* Account + company badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, paddingRight: 24 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: "#374151", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {opp.accountName}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3, flexShrink: 0,
          background: coyCfg.bg, color: coyCfg.color, border: `1px solid ${coyCfg.border}`,
        }}>{opp.company}</span>
      </div>

      {/* Title */}
      <div style={{ fontSize: 13, color: "#111827", marginBottom: 6, lineHeight: 1.3 }}>
        {opp.title}
      </div>

      {/* Value + probability */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        {opp.expectedValue != null && (
          <span style={{ fontSize: 11, fontWeight: 700, color: stage.color }}>
            {fmt(opp.expectedValue)}
          </span>
        )}
        {opp.probability != null && (
          <span style={{ fontSize: 11, color: "#6b7280" }}>{opp.probability}% likely</span>
        )}
      </div>

      {/* Close date */}
      {opp.expectedCloseDate && (
        <div style={{
          fontSize: 11,
          color: daysLeft != null && daysLeft < 0 ? "#b91c1c" : daysLeft != null && daysLeft <= 14 ? "#c2410c" : "#6b7280",
          marginBottom: 6,
        }}>
          {daysLeft == null ? "" : daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? "Due today" : `${daysLeft}d left`}
          {" · "}{fmtDate(opp.expectedCloseDate)}
        </div>
      )}

      {/* Lost reason snippet */}
      {opp.stage === "LOST" && opp.lostReason && (
        <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic", marginBottom: 6 }}>
          {opp.lostReason}
        </div>
      )}

      {/* Stage action buttons */}
      {isActive && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
          {/* Advance hidden for TRIAL_DONE — use Details → Passed/Failed */}
          {next && !isTrial && (
            <button onClick={() => onAdvance(next)}
              style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #d1d5db", background: "#f9fafb", cursor: "pointer", color: "#374151" }}>
              → {STAGE_MAP[next].label}
            </button>
          )}
          <button onClick={onWon}
            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #bbf7d0", background: "#f0fdf4", cursor: "pointer", color: "#15803d", fontWeight: 600 }}>
            Won
          </button>
          <button onClick={onLost}
            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", color: "#6b7280" }}>
            Lost
          </button>
        </div>
      )}

      {/* Details link */}
      <div style={{ marginTop: 6 }}>
        <button onClick={onDetails}
          style={{
            fontSize: 11, color: "#6366f1", background: "none", border: "none",
            cursor: "pointer", padding: 0, fontWeight: 600,
          }}>
          View Details {isTrial ? "· Trial Outcome" : "›"}
        </button>
      </div>

      {/* Kebab menu */}
      <div style={{ position: "absolute", top: 8, right: 8 }}>
        <button onClick={() => setShowActions(v => !v)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#9ca3af", lineHeight: 1 }}>
          ⋯
        </button>
        {showActions && (
          <div style={{
            position: "absolute", right: 0, top: 22, background: "#fff",
            border: "1px solid #e5e7eb", borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 10, minWidth: 110,
          }}>
            <button onClick={() => { setShowActions(false); onEdit(); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151" }}>
              Edit
            </button>
            <button onClick={() => { setShowActions(false); onDelete(); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#b91c1c" }}>
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PageCrmPipeline({ myPerms, userEmail, onBack }) {
  const showToast = useToast();
  const [opps,       setOpps]       = useState([]);
  const [accounts,   setAccounts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState(null);
  const [modal,      setModal]      = useState(null);
  const canManage = myPerms.includes("CRM_MANAGE");
  const [filterUser, setFilterUser] = useState(canManage ? "" : userEmail);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [o, a] = await Promise.all([api.fetchOpportunities(), api.fetchCrmAccounts()]);
      setOpps(o);
      setAccounts(a.sort((x, y) => x.name.localeCompare(y.name)));
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function moveStage(opp, stage, note) {
    await api.updateOpportunity(opp.id, {
      company:           opp.company,
      accountId:         opp.isProspect ? null : opp.accountId,
      prospectName:      opp.isProspect ? opp.accountName : null,
      title:             opp.title,
      stage,
      expectedValue:     opp.expectedValue,
      probability:       opp.probability,
      expectedCloseDate: opp.expectedCloseDate,
      assignedTo:        opp.assignedTo,
      notes:             opp.notes,
      lostReason:        stage === "LOST" ? (note || null) : opp.lostReason,
      stageNote:         note || null,
    });
    showToast("Stage updated");
    setModal(null);
    load();
  }

  async function deleteOpp(opp) {
    if (!window.confirm(`Delete "${opp.title}"?`)) return;
    await api.deleteOpportunity(opp.id);
    showToast("Opportunity deleted", "info");
    load();
  }

  const assignees = [...new Set(opps.map(o => o.assignedTo).filter(Boolean))].sort();
  const filtered  = filterUser ? opps.filter(o => o.assignedTo === filterUser) : opps;

  const byStage = Object.fromEntries(STAGE_KEYS.map(k => [k, []]));
  filtered.forEach(o => { if (byStage[o.stage]) byStage[o.stage].push(o); });

  const activeTotal = ACTIVE_KEYS.reduce((s, k) => s + byStage[k].length, 0);
  const totalValue  = filtered
    .filter(o => ACTIVE_KEYS.includes(o.stage) && o.expectedValue)
    .reduce((s, o) => s + Number(o.expectedValue), 0);

  // Helpers to open stage-note modal for common transitions
  function openAdvanceModal(opp, nextKey) {
    setModal({
      type: "stageNote", opp, toStage: nextKey,
      title: `Move to ${STAGE_MAP[nextKey]?.label}`,
      noteLabel: "Stage notes (optional)",
    });
  }

  function openWonModal(opp) {
    setModal({
      type: "stageNote", opp, toStage: "WON",
      title: "Mark as Won",
      noteLabel: "Closing notes (optional)",
      confirmLabel: "Mark as Won",
      confirmColor: "#15803d",
    });
  }

  function openLostModal(opp) {
    setModal({
      type: "stageNote", opp, toStage: "LOST",
      title: "Mark as Lost",
      noteLabel: "Lost reason (optional)",
      confirmLabel: "Mark as Lost",
      confirmColor: "#6b7280",
    });
  }

  function openPassModal(opp) {
    setModal({
      type: "stageNote", opp, toStage: "PROPOSAL_SENT",
      title: "Trial Passed",
      subtitle: `${opp.title} — ${opp.accountName}`,
      noteLabel: "Notes (optional)",
      confirmLabel: "Passed → Proposal Sent",
      confirmColor: "#15803d",
    });
  }

  function openFailModal(opp) {
    setModal({
      type: "stageNote", opp, toStage: "LEAD",
      title: "Trial Failed",
      subtitle: `${opp.title} — ${opp.accountName}`,
      noteLabel: "Why did the trial fail?",
      noteRequired: true,
      confirmLabel: "Mark as Failed",
      confirmColor: "#b91c1c",
    });
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Sales Pipeline</div>
          <div className="page-subtitle">
            {activeTotal} active · Pipeline value: AED {totalValue.toLocaleString("en-AE", { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {canManage && assignees.length > 0 && (
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }}>
              <option value="">All salespeople</option>
              {assignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          <button className="btn btn-primary" onClick={() => setModal({ type: "create" })}>
            + New Opportunity
          </button>
        </div>
      </div>

      {err && <div className="alert-warn" style={{ marginBottom: 16 }}>{err}</div>}
      {loading && <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</div>}

      {!loading && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "flex", gap: 12, minWidth: STAGES.length * 220, alignItems: "flex-start" }}>
            {STAGES.map(stage => {
              const cards = byStage[stage.key] ?? [];
              return (
                <div key={stage.key} style={{ flex: "0 0 210px", minWidth: 210 }}>
                  <div style={{
                    background: stage.bg, border: `1px solid ${stage.color}22`,
                    borderRadius: 8, padding: "8px 12px", marginBottom: 8,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: stage.color }}>{stage.label}</span>
                    <span style={{
                      background: stage.color, color: "#fff", borderRadius: 12,
                      padding: "1px 7px", fontSize: 11, fontWeight: 700,
                    }}>{cards.length}</span>
                  </div>
                  <div>
                    {cards.length === 0 && (
                      <div style={{ color: "#d1d5db", fontSize: 12, textAlign: "center", padding: "16px 0" }}>—</div>
                    )}
                    {cards.map(opp => (
                      <OppCard key={opp.id} opp={opp}
                        onEdit={() => setModal({ type: "edit", opp })}
                        onAdvance={nextKey => openAdvanceModal(opp, nextKey)}
                        onWon={() => openWonModal(opp)}
                        onLost={() => openLostModal(opp)}
                        onDetails={() => setModal({ type: "details", opp })}
                        onDelete={() => deleteOpp(opp)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {(modal?.type === "create" || modal?.type === "edit") && (
        <OppModal
          opp={modal.type === "edit" ? modal.opp : null}
          accounts={accounts}
          userEmail={userEmail}
          canManage={canManage}
          onSave={() => { showToast("Opportunity saved"); setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}

      {/* Stage note modal (advance / won / lost / trial pass / trial fail) */}
      {modal?.type === "stageNote" && (
        <StageNoteModal
          title={modal.title}
          subtitle={modal.subtitle}
          noteLabel={modal.noteLabel}
          noteRequired={modal.noteRequired}
          confirmLabel={modal.confirmLabel}
          confirmColor={modal.confirmColor}
          onConfirm={note => moveStage(modal.opp, modal.toStage, note)}
          onClose={() => setModal(null)}
        />
      )}

      {/* Details modal */}
      {modal?.type === "details" && (
        <DetailsModal
          opp={modal.opp}
          onPass={() => openPassModal(modal.opp)}
          onFail={() => openFailModal(modal.opp)}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
